import React from 'react';
import {
  CurrencyDollarIcon,
  ClockIcon,
  ReceiptPercentIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import BillingKpiCard from './BillingKpiCard';
import type { TimeEntryStatistics, ExpenseStatistics, PaymentStatistics } from '../types/billing.types';

interface BillingKpiSummaryBarProps {
  timeStats: TimeEntryStatistics | null;
  expenseStats: ExpenseStatistics | null;
  paymentStats?: PaymentStatistics | null;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const BillingKpiSummaryBar: React.FC<BillingKpiSummaryBarProps> = ({ timeStats, expenseStats, paymentStats }) => {
  const unbilledWip = timeStats ? Number(timeStats.total_amount) || 0 : 0;
  const billableHours = timeStats ? Number(timeStats.billable_hours) || 0 : 0;
  const totalExpenses = expenseStats ? Number(expenseStats.total_amount) || 0 : 0;
  const pendingApproval =
    (Number(timeStats?.pending_approval) || 0) + (Number(expenseStats?.pending_approval) || 0);
  const totalReceived = paymentStats ? Number(paymentStats.total_received) || 0 : 0;
  const paymentCount = paymentStats?.total_count || 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <BillingKpiCard
        title="Unbilled WIP"
        value={formatCurrency(unbilledWip)}
        subtitle={timeStats ? `${timeStats.unbilled_entries} entries` : undefined}
        icon={CurrencyDollarIcon}
        color="blue"
      />
      <BillingKpiCard
        title="Billable Hours"
        value={`${billableHours.toFixed(1)}h`}
        subtitle={timeStats ? `${timeStats.total_entries} total entries` : undefined}
        icon={ClockIcon}
        color="green"
      />
      <BillingKpiCard
        title="Total Expenses"
        value={formatCurrency(totalExpenses)}
        subtitle={expenseStats ? `${expenseStats.total_expenses} items` : undefined}
        icon={ReceiptPercentIcon}
        color="purple"
      />
      <BillingKpiCard
        title="Payments Received"
        value={formatCurrency(totalReceived)}
        subtitle={paymentCount > 0 ? `${paymentCount} payment${paymentCount !== 1 ? 's' : ''}` : undefined}
        icon={BanknotesIcon}
        color="emerald"
      />
      <BillingKpiCard
        title="Pending Approval"
        value={String(pendingApproval)}
        subtitle="Awaiting review"
        icon={ExclamationTriangleIcon}
        color={pendingApproval > 0 ? 'amber' : 'green'}
      />
    </div>
  );
};

export default BillingKpiSummaryBar;
